import { Suspense, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ClipboardListIcon } from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { useNgoProfileQuery } from '@/lib/data-hooks';
import { NgoNoticeBar, NgoNoticeDialog, NgoNoticeDialogAction } from './ngo-notice';

const SEEN_KEY = 'ngo-legal-representative-notice-seen';
const BAR_KEY = 'ngo-legal-representative-bar-dismissed';

/**
 * `sessionStorage` is per tab, so it outlives both a sign-out and a switch between NGOs. Called
 * wherever the session changes, beside the query cache that is cleared for the same reason — the
 * browser is not told which NGO it is in, so this cannot be keyed by one.
 */
export function forgetLegalRepresentativeNotice(): void {
	try {
		sessionStorage.removeItem(SEEN_KEY);
		sessionStorage.removeItem(BAR_KEY);
	} catch {
		// Nothing to forget when the browser refuses storage: the dialog was never hidden.
	}
}

/**
 * Shown on every entry while the NGO owes us a representative (FR-72): the bar stays for the whole
 * visit, the dialog opens once per session. A mail opened a week late by somebody who then signs in
 * and finds nothing asking anything of them converts far worse.
 */
export function LegalRepresentativeBanner() {
	return (
		<Suspense fallback={null}>
			<LegalRepresentativeBannerContent />
		</Suspense>
	);
}

function LegalRepresentativeBannerContent() {
	const { t } = useLingui();
	const ngo = useNgoProfileQuery();
	// Dismissing the bar is for the visit, never for good: FR-72 asks on every entry, and a new session is
	// a new entry. Its own key, so putting the dialog away does not take the bar with it.
	const [dismissed, setDismissed] = useState(() => {
		try {
			return sessionStorage.getItem(BAR_KEY) !== null;
		} catch {
			return false;
		}
	});

	if (!ngo.awaitsLegalRepresentative) return null;

	const dismiss = () => {
		setDismissed(true);
		try {
			sessionStorage.setItem(BAR_KEY, '1');
		} catch {
			// A browser refusing storage shows the bar again next entry, which is the safe direction.
		}
	};

	return (
		<>
			{!dismissed && (
				<NgoNoticeBar
					icon={<ClipboardListIcon className="size-4 text-[#35433F]" strokeWidth={1.5} />}
					actions={
						<>
							<Button
								size="sm"
								variant="ghost"
								onClick={dismiss}
								className="shrink-0 hover:bg-white/10 hover:text-white"
							>
								{t`Remind me later`}
							</Button>
							<Button asChild size="sm" variant="secondary" className="shrink-0">
								<Link to="/ngos/settings" search={{ tab: 'contacts' }} hash="legal-representative">
									{t`Review now`}
								</Link>
							</Button>
						</>
					}
				>
					<span className="text-base leading-[21px] font-semibold">{t`ACTION REQUIRED:`}</span>{' '}
					<span className="text-sm leading-[18px] font-medium opacity-60">
						{t`Review the legal representative's details. This is essential for the contract renewal.`}
					</span>
				</NgoNoticeBar>
			)}
			<LegalRepresentativeDialog />
		</>
	);
}

function LegalRepresentativeDialog() {
	const { t } = useLingui();
	// A modal on every navigation would nag a phase that blocks nothing, so it is held for the visit
	// and forgotten whenever the session changes.
	const [open, setOpen] = useState(() => {
		try {
			return sessionStorage.getItem(SEEN_KEY) === null;
		} catch {
			return false;
		}
	});

	const dismiss = () => {
		setOpen(false);
		try {
			sessionStorage.setItem(SEEN_KEY, '1');
		} catch {
			// A browser refusing storage shows the dialog again next entry, which is the safe direction.
		}
	};

	return (
		<NgoNoticeDialog
			open={open}
			onDismiss={dismiss}
			title={t`Important notice`}
			description={t`Please, complete the legal representative details. This is necessary for the contract renewal.`}
			dismissLabel={t`Remind me later`}
			action={
				<NgoNoticeDialogAction onClick={dismiss}>
					<Link to="/ngos/settings" search={{ tab: 'contacts' }} hash="legal-representative">
						{t`Review now`}
					</Link>
				</NgoNoticeDialogAction>
			}
		/>
	);
}
